import moment from 'moment';

export interface DayHours {
    open?: string;
    close?: string;
    isClosed: boolean;
}

export interface BusinessHours {
    monday?: DayHours;
    tuesday?: DayHours;
    wednesday?: DayHours;
    thursday?: DayHours;
    friday?: DayHours;
    saturday?: DayHours;
    sunday?: DayHours;
}

/**
 * Default business hours: Monday-Friday open, Saturday-Sunday closed.
 * Used as fallback when no store-specific businessHours are provided.
 */
const DEFAULT_BUSINESS_HOURS: BusinessHours = {
    sunday: { isClosed: true },
    monday: { isClosed: false },
    tuesday: { isClosed: false },
    wednesday: { isClosed: false },
    thursday: { isClosed: false },
    friday: { isClosed: false },
    saturday: { isClosed: true },
};

const DAY_NAMES: (keyof BusinessHours)[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Formats a UTC date/time value into the viewer's local time using moment's
 * localized "lll" format (e.g. "Jan 1, 2026 12:00 PM"). Returns "" for falsy input.
 */
export function formatDateAndTime(value: Date | string | null | undefined): string {
    if (!value) {
        return "";
    }
    return moment.utc(value).local().format("lll");
}

/**
 * Adds a specified number of business days to a date, respecting store operating schedules.
 *
 * @param date - The starting date
 * @param businessDays - Number of business days to add (must be non-negative)
 * @param businessHours - Optional store-specific business hours. If not provided,
 *                        defaults to Mon-Fri (skipping Saturday/Sunday).
 * @returns A moment object representing the resulting date
 * @throws Error if businessDays is negative, NaN, or Infinity
 *
 * @example
 * // Add 1 business day from Friday (default Mon-Fri schedule) → Monday
 * addBusinessDays(new Date('2025-01-03'), 1); // Returns Monday 2025-01-06
 *
 * @example
 * // Add 1 business day with 7-day store schedule → Saturday
 * const sevenDayStore = { saturday: { isClosed: false }, sunday: { isClosed: false }, ... };
 * addBusinessDays(new Date('2025-01-03'), 1, sevenDayStore); // Returns Saturday 2025-01-04
 */
export function addBusinessDays(
    date: Date | moment.Moment,
    businessDays: number,
    businessHours?: BusinessHours
): moment.Moment {
    // Input validation
    if (!Number.isFinite(businessDays) || businessDays < 0) {
        throw new Error('businessDays must be a non-negative finite number');
    }

    const result = moment(date);

    if (businessDays === 0) {
        return result;
    }

    // Use store-specific hours if provided, otherwise default to Mon-Fri
    let hours = businessHours ?? DEFAULT_BUSINESS_HOURS;

    // An all-closed schedule would loop forever — treat it as unusable and fall back to the default.
    if (!DAY_NAMES.some(d => !hours[d]?.isClosed)) {
        hours = DEFAULT_BUSINESS_HOURS;
    }

    let daysAdded = 0;
    while (daysAdded < businessDays) {
        result.add(1, 'day');

        if (isBusinessDay(result, hours)) {
            daysAdded++;
        }
    }

    return result;
}

/**
 * Determines if a given date is a business day based on the provided business hours.
 *
 * @param date - The date to check
 * @param businessHours - Business hours configuration. If not provided, all days are considered business days.
 * @returns True if the date falls on a business day, false otherwise
 */
function isBusinessDay(date: moment.Moment, businessHours?: BusinessHours): boolean {
    if (!businessHours) {
        return true;
    }

    const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Map day of week to BusinessHours property
    const dayName = DAY_NAMES[dayOfWeek];
    const dayHours = businessHours[dayName];

    // If day is not configured, assume it's a business day (open by default)
    // If day is configured and marked as closed, it's not a business day
    if (dayHours?.isClosed) {
        return false;
    }

    return true;
}
